import React from 'react';
import { motion } from 'framer-motion';

interface GPUMemoryGaugeProps {
  used: number;
  total: number;
}

const GPUMemoryGauge: React.FC<GPUMemoryGaugeProps> = ({ used, total }) => {
  const percentage = total > 0 ? (used / total) * 100 : 0;
  const usedGB = (used / 1024).toFixed(1);
  const totalGB = (total / 1024).toFixed(1);
  
  // Calculate stroke color based on usage - more subtle colors
  const getColor = () => {
    if (percentage > 90) return '#ef4444'; // red
    if (percentage > 75) return '#fbbf24'; // yellow
    if (percentage > 50) return '#60a5fa'; // blue
    return '#10b981'; // green
  };

  const strokeColor = getColor();
  const radius = 35;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="bg-black/30 backdrop-blur-sm p-3 border border-gray-800/50 h-full flex flex-col">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Memory</h3>
      
      <div className="flex-1 flex items-center justify-center relative">
        <svg width="90" height="90" className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="45"
            cy="45"
            r={radius}
            stroke="#374151"
            strokeWidth="8"
            fill="none"
            opacity="0.3"
          />
          
          {/* Progress circle */}
          <motion.circle
            cx="45"
            cy="45"
            r={radius}
            stroke={strokeColor}
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{
              strokeDasharray: circumference,
              filter: `drop-shadow(0 0 8px ${strokeColor}40)`
            }}
          />
        </svg>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div
            key={percentage}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="text-lg font-bold" style={{ color: strokeColor }}>
              {percentage.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-400">
              {usedGB}/{totalGB}GB
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GPUMemoryGauge;